import { Palette, STAGE_BG_TEXTURE, STAGE_BG_FILE } from '@core/config/constants';

/** Id del escenario especial que rota al azar entre los desbloqueados. */
export const RANDOM_BACKGROUND_ID = 'random';

/** Como se dibuja un escenario: una imagen cargada o un degradado de dos colores. */
export type BackgroundKind =
  | { readonly type: 'image'; readonly texture: string; readonly file: string }
  | { readonly type: 'gradient'; readonly top: number; readonly bottom: number };

/** Un escenario comprable/equipable que cambia el fondo de las pantallas. */
export interface BackgroundOption {
  /** Identificador unico (se guarda en el perfil). */
  readonly id: string;
  /** Nombre visible. */
  readonly name: string;
  /** Emoji que representa el escenario en la tarjeta. */
  readonly emoji: string;
  /** Precio en estrellas (0 = gratis, ya desbloqueado). */
  readonly price: number;
  /** Color de la tarjeta en el selector. */
  readonly cardColor: number;
  /** Definicion del fondo a dibujar. */
  readonly background: BackgroundKind;
}

/**
 * Catalogo de escenarios. El primero ('stage') es gratis y viene desbloqueado por defecto.
 * Para agregar uno nuevo, sumar un objeto aca (no requiere tocar nada mas).
 */
export const BACKGROUNDS: readonly BackgroundOption[] = [
  {
    id: 'stage',
    name: 'Escenario',
    emoji: '🎤',
    price: 0,
    cardColor: Palette.secondary,
    background: { type: 'image', texture: STAGE_BG_TEXTURE, file: STAGE_BG_FILE },
  },
  {
    id: 'dream',
    name: 'Show de Ensueño',
    emoji: '💖',
    price: 80,
    cardColor: Palette.primary,
    background: { type: 'image', texture: 'bg-skyline', file: 'bg-skyline.png' },
  },
  {
    id: 'forest',
    name: 'Bosque Mágico',
    emoji: '🌳',
    price: 85,
    cardColor: 0x2ecc71,
    background: { type: 'image', texture: 'bg-forest', file: 'bg-forest.png' },
  },
  {
    id: 'city',
    name: 'Ciudad Neón',
    emoji: '🏙️',
    price: 95,
    cardColor: Palette.secondary,
    background: { type: 'image', texture: 'bg-city', file: 'bg-city.png' },
  },
  {
    id: 'beach-img',
    name: 'Playa Tropical',
    emoji: '🌴',
    price: 90,
    cardColor: Palette.accent,
    background: { type: 'image', texture: 'bg-beach', file: 'bg-beach.png' },
  },
  {
    id: 'winter',
    name: 'País de Nieve',
    emoji: '❄️',
    price: 100,
    cardColor: 0x4dd0e1,
    background: { type: 'image', texture: 'bg-winter', file: 'bg-winter.png' },
  },
  {
    id: 'neon',
    name: 'Noche Neón',
    emoji: '🌃',
    price: 30,
    cardColor: Palette.primary,
    background: { type: 'gradient', top: 0x2a0d4e, bottom: 0xff2d8e },
  },
  {
    id: 'sunset',
    name: 'Atardecer',
    emoji: '🌅',
    price: 45,
    cardColor: Palette.highlight,
    background: { type: 'gradient', top: 0xff7e5f, bottom: 0x7c4dff },
  },
  {
    id: 'beach',
    name: 'Playa',
    emoji: '🏖️',
    price: 55,
    cardColor: Palette.accent,
    background: { type: 'gradient', top: 0x4dd0e1, bottom: 0xffd54f },
  },
  {
    id: 'galaxy',
    name: 'Galaxia',
    emoji: '🌌',
    price: 70,
    cardColor: Palette.secondary,
    background: { type: 'gradient', top: 0x0d0b2a, bottom: 0x6a1f73 },
  },
  {
    id: 'candy',
    name: 'Caramelo',
    emoji: '🍬',
    price: 90,
    cardColor: Palette.primary,
    background: { type: 'gradient', top: 0xff9ec7, bottom: 0x7c4dff },
  },
  {
    id: RANDOM_BACKGROUND_ID,
    name: 'Sorpresa',
    emoji: '🎲',
    price: 100,
    cardColor: Palette.accent,
    // Su fondo no se dibuja directamente: al equiparlo se elige al azar uno de los
    // escenarios desbloqueados cada vez que se entra a una pantalla (rota solo).
    background: { type: 'gradient', top: 0x4dd0e1, bottom: 0xff2d8e },
  },
];

/** Devuelve un escenario por su id (o undefined si no existe). */
export function findBackground(id: string): BackgroundOption | undefined {
  return BACKGROUNDS.find((option) => option.id === id);
}
